// Fiches de décision d'architecture (docs/COMPLEMENT-RD.md § 11) : pré-remplissage, contrôles, statut de partage
// de chaque raison, et ce qui sortirait de chez le client. Stockées dans la carte par l'API (objet « decision »).
import type {
  AttributsDecision,
  Carte,
  Correction,
  FourchetteEur,
  ObjetConstat,
  ObjetDecision,
  OptionDecision,
  Raison,
  RetourExperience,
  StatutPartage,
} from '../api/types';
import { lireManques, type ManqueAffiche } from './manques';
import { dateFr, fourchette } from './format';
import { importMarque } from './importation';
import { slug } from './corrections';

/** Catégories structurées : seules elles peuvent sortir de chez le client (le texte libre reste privé). */
export const CATEGORIES: { id: string; libelle: string; generalise: string }[] = [
  { id: 'budget', libelle: 'Budget', generalise: 'Contrainte de budget' },
  { id: 'delai', libelle: 'Délai', generalise: 'Contrainte de délai' },
  { id: 'competences', libelle: 'Compétences et effectifs', generalise: 'Compétences ou effectifs pour exploiter' },
  { id: 'exigence_reglementaire', libelle: 'Exigence réglementaire', generalise: 'Exigence réglementaire' },
  {
    id: 'exigence_donneur_ordres',
    libelle: 'Exigence d’un donneur d’ordres',
    generalise: 'Exigence contractuelle d’un donneur d’ordres',
  },
  { id: 'exigence_assureur', libelle: 'Exigence d’un assureur', generalise: 'Exigence d’un assureur' },
  { id: 'compatibilite', libelle: 'Compatibilité avec l’existant', generalise: 'Compatibilité avec l’existant' },
  { id: 'exploitation', libelle: 'Exploitation en continu', generalise: 'Contrainte d’exploitation' },
  { id: 'fenetre_arret', libelle: 'Fenêtre d’arrêt de l’usine', generalise: 'Fenêtre d’arrêt de production' },
  { id: 'surete', libelle: 'Sûreté des installations', generalise: 'Sûreté des installations' },
  { id: 'souverainete', libelle: 'Souveraineté ou qualification', generalise: 'Souveraineté ou qualification exigée' },
  { id: 'contrat_en_cours', libelle: 'Contrat en cours', generalise: 'Contrat en cours' },
  { id: 'fournisseur_reference', libelle: 'Fournisseur référencé', generalise: 'Fournisseur référencé' },
  { id: 'risque', libelle: 'Réduction du risque', generalise: 'Réduction du risque' },
];

export const CATEGORIE_PAR_ID = new Map(CATEGORIES.map((c) => [c.id, c]));

export function estStructuree(categorie: string | null | undefined): boolean {
  return !!categorie && CATEGORIE_PAR_ID.has(categorie);
}

/** Statut effectif : une raison sans catégorie structurée reste privée, quel que soit le choix affiché. */
export function partageEffectif(r: Raison): StatutPartage {
  const p = r.partage ?? 'prive';
  return p !== 'prive' && !estStructuree(r.categorie) ? 'prive' : p;
}

/** Ce qui sortirait de chez le client pour une raison (null : rien ne sort). */
export function ceQuiSort(r: Raison): { categorie: string; poids?: string } | null {
  const p = partageEffectif(r);
  if (p === 'prive' || !r.categorie) return null;
  const c = CATEGORIE_PAR_ID.get(r.categorie);
  if (!c) return null;
  if (p === 'partageable') return { categorie: c.libelle, poids: r.poids ?? 'importante' };
  return { categorie: c.generalise };
}

export interface Probleme {
  champ: string;
  message: string;
  bloquant: boolean;
}

export function controler(nom: string, a: AttributsDecision): Probleme[] {
  const out: Probleme[] = [];
  if (!nom.trim()) out.push({ champ: 'nom', message: 'Donnez un titre à la décision.', bloquant: true });
  if (!a.contexte?.trim()) out.push({ champ: 'contexte', message: 'Le contexte est vide.', bloquant: true });
  if (!a.declencheur?.trim()) out.push({ champ: 'declencheur', message: 'Le déclencheur est vide.', bloquant: true });
  const options = a.options ?? [];
  if (options.length < 2)
    out.push({
      champ: 'options',
      message: 'Étudiez au moins deux options, dont la plus petite qui règle le problème.',
      bloquant: true,
    });
  options.forEach((o, i) => {
    if (!o.libelle.trim())
      out.push({ champ: `options.${i}`, message: `L’option ${i + 1} n’a pas de libellé.`, bloquant: true });
  });
  const retenues = options.filter((o) => o.retenue);
  if (retenues.length > 1) out.push({ champ: 'options', message: 'Une seule option peut être retenue.', bloquant: true });
  if (a.decide_le && retenues.length === 0)
    out.push({ champ: 'options', message: 'Une décision datée doit retenir une option.', bloquant: true });
  for (const o of retenues) {
    if ((o.raisons ?? []).length === 0)
      out.push({ champ: 'raisons', message: `Donnez toutes les raisons du choix « ${o.libelle} ».`, bloquant: true });
  }
  if (retenues.length === 1) {
    for (const o of options.filter((x) => !x.retenue)) {
      if ((o.raisons_rejet ?? []).length === 0)
        out.push({
          champ: 'raisons_rejet',
          message: `Dites ce qui a éliminé « ${o.libelle} ».`,
          bloquant: false,
        });
    }
  }
  for (const o of options) {
    for (const r of [...(o.raisons ?? []), ...(o.raisons_rejet ?? [])]) {
      if (!r.texte.trim()) out.push({ champ: 'raisons', message: 'Une raison est vide.', bloquant: true });
      if ((r.partage ?? 'prive') !== 'prive' && !estStructuree(r.categorie))
        out.push({
          champ: 'partage',
          message: `« ${r.texte || 'raison sans texte'} » n’a pas de catégorie structurée : elle restera privée.`,
          bloquant: false,
        });
    }
  }
  return out;
}

export function idDecision(nom: string): string {
  return `decision:${slug(nom)}`;
}

/** Fiche pré-remplie par Ekera à partir d'un constat ; le DSI la corrige et la complète. */
export function preRemplir(carte: Carte, constat: ObjetConstat, aujourdhui: string): { nom: string; attributs: AttributsDecision } {
  const e = carte.entreprise;
  const ea = (e?.attributs ?? {}) as Record<string, unknown>;
  const ca = (constat.attributs ?? {}) as Record<string, unknown>;
  const perte = ca.perte_estimee as FourchetteEur | undefined;
  const sites = Array.isArray(ea.sites) ? ea.sites.length : null;
  const morceaux = [
    e?.nom,
    typeof ea.activite === 'string' ? ea.activite : null,
    typeof ea.effectif === 'number' ? `${ea.effectif} salariés` : null,
    sites ? `${sites} sites` : null,
    typeof ea.nis2 === 'string' ? `NIS2 : ${ea.nis2}` : null,
  ].filter(Boolean);
  const exigence = typeof ca.exigence === 'string' ? ` (${ca.exigence})` : '';
  const perteTxt = perte ? ` ; pertes potentielles estimées si exploité : ${fourchette(perte.bas.valeur, perte.haut.valeur)}` : '';
  const recommandation = typeof ca.recommandation === 'string' ? ca.recommandation : null;
  const options: OptionDecision[] = [
    { libelle: recommandation ?? `Corriger au plus simple : ${constat.nom}`, retenue: false, raisons: [], raisons_rejet: [] },
    { libelle: 'Accepter le risque et le suivre, sans travaux', retenue: false, raisons: [], raisons_rejet: [] },
  ];
  return {
    nom: `Traiter : ${constat.nom}`,
    attributs: {
      contexte: `${morceaux.join(' ; ')}.`,
      declencheur: `Constat d’Ekera du ${aujourdhui} : ${constat.nom}${exigence}${perteTxt}.`,
      options,
      perte_evitee: perte ?? null,
      retours_experience: [],
      constat: constat.id,
    },
  };
}

export function correctionCreerDecision(
  nom: string,
  attributs: AttributsDecision,
  o: { auteur: string },
): Correction {
  const contenu: Pick<ObjetDecision, 'id' | 'type' | 'nom' | 'attributs'> = {
    id: idDecision(nom),
    type: 'decision',
    nom,
    attributs: attributsModifiablesDecision(attributs),
  };
  return { operation: 'creer', contenu: contenu as Record<string, unknown>, auteur: o.auteur };
}

/** CV-S1 : la fin du circuit produit ces champs ; le formulaire ne les écrit jamais. */
export function attributsModifiablesDecision(attributs: AttributsDecision): AttributsDecision {
  return Object.fromEntries(Object.entries(attributs).filter(([cle]) =>
    !['statut', 'decideurs', 'validee_par', 'validee_le', 'decide_le'].includes(cle),
  ));
}

export function nouvelleRaison(): Raison {
  return { texte: '', categorie: null, poids: 'importante', partage: 'prive' };
}

// ---------------------------------------------------------------- retours d'expérience à 3, 6 et 12 mois

export type EtatRetour = 'sans_date' | 'a_venir' | 'a_recueillir' | 'recueilli';

export function echeance(decideLe: string, mois: 3 | 6 | 12): string {
  const d = new Date(`${decideLe.slice(0, 10)}T00:00:00Z`);
  const jour = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + mois);
  const dernier = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(jour, dernier));
  return d.toISOString().slice(0, 10);
}

export function etatRetour(
  a: AttributsDecision,
  mois: 3 | 6 | 12,
  aujourdhui: string,
): { etat: EtatRetour; date: string | null; retour?: RetourExperience } {
  const retour = (a.retours_experience ?? []).find((r) => r.a_mois === mois);
  if (!a.decide_le) return { etat: retour ? 'recueilli' : 'sans_date', date: null, retour };
  const date = echeance(a.decide_le, mois);
  if (retour) return { etat: 'recueilli', date, retour };
  return { etat: date <= aujourdhui ? 'a_recueillir' : 'a_venir', date };
}

const LIBELLE_ATTRIBUT: Record<string, string> = {
  contexte: 'le contexte',
  declencheur: 'le déclencheur',
  options: 'les options',
  decideurs: 'les décideurs',
  decide_le: 'la date de décision',
  perte_evitee: 'les pertes potentielles évitées',
  retours_experience: 'les retours d’expérience',
  constat: 'le constat lié',
};

export interface CorrectionFiche {
  /** Libellés lisibles des attributs corrigés (vide : la fiche entière). */
  attributs: string[];
  par: string | null;
  le: string | null;
}

/**
 * Fiche pré-remplie par Ekera au diagnostic : ses euros (coût des options, pertes potentielles évitées) sont un
 * instantané du moteur à cette date, alors que les scénarios d'investissement proposés du tableau de bord sont
 * recalculés sur la carte actuelle (docs/CONTRAT.md, relecture P4).
 * Rend `{ date, corrections }` (date AAAA-MM-JJ ou null si la fiche ne la porte pas ; corrections : ce que le DSI a
 * corrigé depuis, P12) quand la fiche vient d'Ekera, sinon null.
 */
export function instantaneDiagnostic(
  el:
    | (Pick<ObjetDecision, 'extensions'> & Partial<Pick<ObjetDecision, 'provenance' | 'provenance_attributs'>>)
    | null
    | undefined,
): { date: string | null; corrections: CorrectionFiche[]; manques?: ManqueAffiche[] } | null {
  const fiche = (el?.extensions as Record<string, unknown> | undefined)?.ekera_decisions;
  if (!fiche || typeof fiche !== 'object') return null;
  const par = (fiche as Record<string, unknown>).preremplie_par;
  if (typeof par !== 'string' || !/^ekera\b/i.test(par)) return null;
  const m = /\b(\d{4}-\d{2}-\d{2})\b/.exec(par);
  // Corrections du DSI depuis le diagnostic : entrées de `provenance_attributs` de méthode « dsi » (ou « corrige »),
  // regroupées par auteur et date ; une provenance d'objet corrigée par le DSI vaut correction de la fiche entière.
  const groupes = new Map<string, CorrectionFiche>();
  const ajoute = (libelle: string | null, p: { valide_par?: string | null; valide_le?: string | null }) => {
    const cle = `${p.valide_par ?? ''}|${p.valide_le ?? ''}`;
    const g = groupes.get(cle) ?? { attributs: [], par: p.valide_par ?? null, le: p.valide_le ?? null };
    if (libelle && !g.attributs.includes(libelle)) g.attributs.push(libelle);
    groupes.set(cle, g);
  };
  const corrigee = (p: { methode?: string | null; statut?: string | null; extensions?: unknown } | undefined) =>
    !!p && !importMarque(p as never) && (p.methode === 'dsi' || p.statut === 'corrige');
  const pa = (el?.provenance_attributs ?? {}) as Record<string, NonNullable<ObjetDecision['provenance']>>;
  for (const [cle, p] of Object.entries(pa)) if (corrigee(p)) ajoute(LIBELLE_ATTRIBUT[cle] ?? cle.replace(/_/g, ' '), p);
  const po = el?.provenance;
  if (po?.methode === 'dsi') {
    // Un objet créé par une écriture du DSI n'est jamais présenté comme venant d'Ekera (relecture P9, l'API refuse
    // aussi ce marqueur à la création) : seul un objet « corrigé » par le DSI garde le bandeau, avec sa correction.
    if (po.statut !== 'corrige') return null;
    if (!Object.keys(pa).length || !groupes.size) ajoute(null, po);
  }
  const manques = (fiche as Record<string, unknown>).manques;
  return {
    date: m?.[1] ?? null,
    corrections: [...groupes.values()],
    ...(manques !== undefined ? {
      manques: lireManques(manques).map((m) => ({ ...m, objet: `composant:${m.objet}` })),
    } : {}),
  };
}

/** « corrigés depuis par Thomas Garnier le 21/10/2026 (les options, les pertes potentielles évitées) » ; chaîne vide si rien. */
export function texteCorrections(corrections: CorrectionFiche[]): string {
  return corrections
    .map(
      (c) =>
        `corrigés depuis par ${c.par ?? 'le DSI'}${c.le ? ` le ${dateFr(c.le)}` : ''} (${c.attributs.length ? c.attributs.join(', ') : 'fiche entière'})`,
    )
    .join(' ; ');
}
